import {
  Badge,
  Flex,
  Text,
  Center,
  Box,
  Select,
  UnorderedList,
  ListItem,
  Link,
  HStack,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";

import { getCorrection, getGgTranslation } from "apis";
import { apis } from "utils/config";
import {
  generateColor,
  generateSuggestionKey,
  generateSuggestionUrl,
  getFullType,
} from "utils/functions";

import FadeIn from "./FadeIn";
import Loading from "./Loading";
import { Suggestion } from "utils/types";
import { PlaySound } from "./PlaySound";

const options = [
  { value: "en", label: "English" },
  { value: "zh", label: "Chinese" },
  { value: "id", label: "Bahasa Indonesia" },
  { value: "ms", label: "Bahasa Malaysia" },
  { value: "th", label: "Thai" },
  { value: "my", label: "Burmese" },
  { value: "vi", label: "Vietnamese" },
  { value: "km", label: "Khmer" },
];

type DefinitionsProps = {
  data: any;
  isLoading: boolean;
  language: string;
  onChangeLanguage: (lang: string) => void;
  keyword?: string;
  google?: { translatedText: string }[];
  pos?: string;
  word?: string;
};

const Definitions = ({
  data,
  isLoading,
  language,
  onChangeLanguage,
  keyword,
  google = [],
  pos = "",
  word,
}: DefinitionsProps) => {
  const formattedPos = useMemo(() => getFullType(pos), [pos]);
  const allDefinitions = useMemo(() => {
    const allDefinitions = data?.reduce?.((res: any[], cur: any) => {
      const matched =
        cur.meanings?.find((o: any) => o.partOfSpeech === formattedPos)
          ?.definitions || [];
      return [...res, ...matched];
    }, []);
    return allDefinitions;
  }, [data, formattedPos]);

  const audio = useMemo(() => {
    return data?.[0]?.phonetics?.[0]?.audio;
  }, [data]);

  const { data: correctionData, isFetching: isFetchingCorrection } = useQuery({
    queryKey: [apis.correction(keyword).key, keyword],
    queryFn: () => getCorrection(keyword as string),
    enabled: !!keyword && isEmpty(allDefinitions),
  });
  const { data: translatedKeyword, isFetching: fetchingTranslatedKeyword } =
    useQuery({
      queryKey: [apis.ggTranslation().key, keyword, language],
      queryFn: () => getGgTranslation(keyword as string, language as string),
      enabled: !!keyword && language !== "en",
    });

  let shouldShowNotFound = useMemo(() => {
    const data = correctionData?.data;
    if (data?.length > 0) {
      return !!data?.find((o: Suggestion) => o.word === keyword);
    }
    return false;
  }, [correctionData?.data, keyword]);

  const generateGoogleTranslate = () => {
    return google?.map((item, index) => {
      const formattedPos = getFullType(pos);
      return (
        <Flex
          as="li"
          key={index}
          mb="0.5rem"
          fontSize="sm"
          alignItems="flex-start"
        >
          <Badge variant="subtle" colorScheme={generateColor(formattedPos)}>
            {formattedPos}
          </Badge>
          <Text ml="0.5rem" color="grayTitle">
            {item.translatedText}
          </Text>
        </Flex>
      );
    });
  };

  return (
    <FadeIn>
      <Flex justifyContent="flex-end">
        <Flex flex="1" color="primary">
          {!fetchingTranslatedKeyword && (
            <Text fontSize="3xl">
              {isEmpty(allDefinitions)
                ? word
                : translatedKeyword?.data?.translations?.[0]?.translatedText ||
                  word}
            </Text>
          )}
        </Flex>
        <Box maxW="200px" hidden>
          <Select
            value={language}
            onChange={(e: any) => {
              onChangeLanguage(e.target.value);
            }}
            mt="0.25rem"
            fontSize="sm"
            color="grayTitle"
            height="1.5rem"
          >
            {options.map((option) => (
              <Box
                as="option"
                key={option.value}
                value={option.value}
                color="grayTxt"
              >
                {option.label}
              </Box>
            ))}
          </Select>
        </Box>
      </Flex>
      <Box my="4">
        {isLoading || isFetchingCorrection ? (
          <Loading />
        ) : isEmpty(allDefinitions) ? (
          !!correctionData?.data?.length ? (
            shouldShowNotFound ? (
              google?.length > 0 ? (
                generateGoogleTranslate()
              ) : (
                <Text color="grayTitle" as="h6" size="md" mb="1rem">
                  {`Cannot find anything related to "${keyword}" for this language`}
                </Text>
              )
            ) : (
              <Box>
                <Text color="grayTitle" as="h6" size="md" mb="1rem">
                  Are you looking for
                </Text>
                <UnorderedList pl="1rem">
                  {correctionData?.data?.map((o: Suggestion) => {
                    return (
                      <ListItem key={generateSuggestionKey(o)} color="grayTxt">
                        <Link as={RouterLink} to={generateSuggestionUrl(o)}>
                          <Box display="flex">
                            <Text fontSize="sm">{o.word}</Text>
                            {o.pos && (
                              <Text ml="0.1rem" fontSize="0.6rem">
                                {o.pos}
                              </Text>
                            )}
                          </Box>
                        </Link>
                      </ListItem>
                    );
                  })}
                </UnorderedList>
              </Box>
            )
          ) : (
            <Center my="4">No Data</Center>
          )
        ) : (
          <ol>
            {allDefinitions?.map((item: any, index: number) => {
              return (
                <Flex
                  as="li"
                  key={item.definition}
                  mb="0.5rem"
                  fontSize="sm"
                  alignItems="flex-start"
                >
                  <HStack>
                    <Badge
                      variant="subtle"
                      colorScheme={generateColor(formattedPos)}
                      minW="5rem"
                      textAlign="center"
                    >
                      {formattedPos}
                    </Badge>
                    {!!audio && <PlaySound src={audio || ""} />}
                  </HStack>
                  <Text ml="0.5rem" color="grayTitle">
                    {item.definition}
                  </Text>
                </Flex>
              );
            })}
          </ol>
        )}
      </Box>
    </FadeIn>
  );
};

export default Definitions;
